/**
 * トレンド分析カード
 * @description 30日間の実績と7日間の予測を表示するグラフ
 */

import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  TrendingUp,
  BarChart,
  Users,
  DollarSign,
  Info,
  Circle as CircleIcon
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  ReferenceLine,
  Legend
} from 'recharts';
import { format, addDays } from 'date-fns';
import { ja } from 'date-fns/locale';
import { useDashboardData } from '@/hooks/useDashboardData';

type MetricType = 'revenue' | 'customers' | 'averageSpend';

// Chart.js関連の型定義
interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    payload: TrendData;
    value: number;
    dataKey: string;
  }>;
}

interface TrendData {
  index: number;
  date: string;
  displayDate: string;
  revenue: number;
  customers: number;
  averageSpend: number;
  // 実績・予測分離フィールド
  revenueActual: number | null;
  revenuePredicted: number | null;
  customersActual: number | null;
  customersPredicted: number | null;
  averageSpendActual: number | null;
  averageSpendPredicted: number | null;
  predicted?: boolean;
  event?: string;
}

/**
 * トレンド分析コンポーネント
 */
export const TrendAnalysis: React.FC = () => {
  const [selectedMetric, setSelectedMetric] = useState<MetricType>('revenue');
  
  // useDashboardData統合（実データ取得）
  const { chartData, loading, error } = useDashboardData();

  // chartDataをTrendData形式に変換（最新30日を実績として使用）
  const transformedData: TrendData[] = useMemo(() => {
    if (!chartData || chartData.length === 0) return [];
    
    // 最新30日分を取得
    const last30Days = chartData.slice(-30);
    
    return last30Days.map((point, index) => {
      const date = new Date(point.date);
      const revenue = point.revenue || 0;
      const customers = point.visitors || 0; // visitorsをcustomersとして使用
      const averageSpend = customers > 0 ? revenue / customers : 0;
      
      return {
        index,
        date: format(date, 'MM/dd'),
        displayDate: format(date, 'M月d日(E)', { locale: ja }),
        revenue,
        customers,
        averageSpend,
        // 実績データフィールド
        revenueActual: revenue,
        revenuePredicted: null,
        customersActual: customers,
        customersPredicted: null,
        averageSpendActual: averageSpend,
        averageSpendPredicted: null,
        predicted: false,
        event: undefined,
      };
    });
  }, [chartData]);

  // 予測データを追加（簡易実装：最後の7日分）
  const trendData = useMemo(() => {
    if (transformedData.length === 0) return [];
    
    // 🔧 FIX: 最後の実績が0の場合は過去30日の平均値を使用
    const lastActual = transformedData[transformedData.length - 1];
    let baseRevenue = lastActual.revenue;
    let baseCustomers = lastActual.customers;
    
    // 最後の実績が0の場合、過去30日の平均値を計算
    if (baseRevenue === 0 || baseCustomers === 0) {
      const totalRevenue = transformedData.reduce((sum, d) => sum + d.revenue, 0);
      const totalCustomers = transformedData.reduce((sum, d) => sum + d.customers, 0);
      const nonZeroDays = transformedData.filter(d => d.revenue > 0 || d.customers > 0).length;
      
      if (nonZeroDays > 0) {
        baseRevenue = totalRevenue / nonZeroDays;
        baseCustomers = totalCustomers / nonZeroDays;
      }
    }
    
    const today = new Date();
    let dataIndex = transformedData.length;
    
    // 予測データ（7日分）を生成
    const forecastDays = Array.from({ length: 7 }, (_, i) => {
      const date = addDays(today, i + 1);
      const growthRate = 1.02; // +2%成長予測
      
      const predictedRevenue = baseRevenue * Math.pow(growthRate, i + 1);
      const predictedCustomers = baseCustomers * 1.01; // +1%顧客数予測
      const predictedAverageSpend = predictedCustomers > 0 ? predictedRevenue / predictedCustomers : 0;
      
      return {
        index: dataIndex++,
        date: format(date, 'MM/dd'),
        displayDate: format(date, 'M月d日(E)', { locale: ja }),
        revenue: Math.floor(predictedRevenue),
        customers: Math.floor(predictedCustomers),
        averageSpend: Math.floor(predictedAverageSpend),
        // 予測データフィールド
        revenueActual: null,
        revenuePredicted: Math.floor(predictedRevenue),
        customersActual: null,
        customersPredicted: Math.floor(predictedCustomers),
        averageSpendActual: null,
        averageSpendPredicted: Math.floor(predictedAverageSpend),
        predicted: true,
        event: undefined,
      };
    });

    return [...transformedData, ...forecastDays];
  }, [transformedData]);

  // メトリクス設定（日本語化 + 動的domain）
  // 🔧 FIX: CSS変数を直接色値に変更（SVGのstroke属性で正しく解決されない問題を回避）
  const metrics = {
    revenue: {
      label: '売上高',
      icon: DollarSign,
      color: '#0ea5e9', // Primary color (Sky 500)
      format: (value: number) => `¥${value.toLocaleString()}`,
      getDomain: (_data: TrendData[]): [number, string] => [0, 'dataMax + 20000']
    },
    customers: {
      label: '来店客数',
      icon: Users,
      color: '#0891b2', // Info color (Cyan 600)
      format: (value: number) => `${value}名`,
      getDomain: (data: TrendData[]): [number, number] => [0, Math.max(...data.map(d => d.customers)) + 10]
    },
    averageSpend: {
      label: '平均単価',
      icon: TrendingUp,
      color: '#10b981', // Success color (Emerald 500)
      format: (value: number) => `¥${Math.floor(value).toLocaleString()}`,
      getDomain: (data: TrendData[]): [number, number] => [0, Math.max(...data.map(d => d.averageSpend)) + 1000]
    }
  };

  const currentMetric = metrics[selectedMetric];

  // X軸tick表示制御（インデックスベース37ポイント全体最適化）
  const xAxisTicks = useMemo(() => {
    const interval = 3; // 3日間隔で細かい表示
    const ticks: number[] = [];

    // 重要インデックスを優先表示
    const importantIndices = [0, 10, 20, 29, 36]; // 開始、中間、予測開始、最終
    ticks.push(...importantIndices);

    // 間隔ベースで追加表示
    for (let i = 0; i < trendData.length; i += interval) {
      if (!ticks.includes(i)) {
        ticks.push(i);
      }
    }

    return [...new Set(ticks)].sort((a, b) => a - b);
  }, [trendData]);

  // インデックスを日付に変換するFormatter
  const formatXAxisTick = (tickItem: number | string): string => {
    const index = parseInt(String(tickItem));
    if (isNaN(index) || index < 0 || index >= trendData.length) return '';
    return trendData[index]?.date || '';
  };

  // カスタムTooltip
  const CustomTooltip = ({ active, payload }: TooltipProps) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload as TrendData;
      return (
        <div className="rounded-lg border bg-card p-3 shadow-lg">
          <p className="text-sm font-semibold mb-1">
            {data.displayDate}
            {data.predicted && (
              <Badge variant="outline" className="ml-2 text-xs h-4 px-1">
                予測
              </Badge>
            )}
          </p>
          <div className="space-y-1">
            {Object.entries(metrics).map(([key, metric]) => {
              const actualKey = `${key}Actual` as keyof TrendData;
              const predictedKey = `${key}Predicted` as keyof TrendData;
              const actualValue = data[actualKey];
              const predictedValue = data[predictedKey];
              const displayValue = actualValue ?? predictedValue;

              if (displayValue === null || displayValue === undefined) return null;

              return (
                <div key={key} className="flex items-center justify-between gap-3">
                  <span className="text-xs text-muted-foreground">{metric.label}</span>
                  <span className="text-sm font-medium">{metric.format(displayValue as number)}</span>
                </div>
              );
            })}
            {data.event && (
              <div className="pt-1 border-t">
                <Badge variant="secondary" className="text-xs">
                  {data.event}
                </Badge>
              </div>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <Card className="h-[400px]">
      <CardHeader>
        {/* ヘッダー */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BarChart className="text-primary" />
            <CardTitle className="text-base font-semibold">
              トレンド分析
            </CardTitle>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Info className="w-4 h-4 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>30日間の実績と7日間の予測を表示</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          <Badge variant="outline" className="hidden sm:inline-flex">
            30日実績 + 7日予測
          </Badge>
        </div>

        {/* メトリクス選択 */}
        <ToggleGroup
          type="single"
          value={selectedMetric}
          onValueChange={(value) => value && setSelectedMetric(value as MetricType)}
          className="justify-start mt-4"
        >
          {(Object.keys(metrics) as MetricType[]).map((metric) => {
            const config = metrics[metric];
            const IconComponent = config.icon;
            return (
              <ToggleGroupItem
                key={metric}
                value={metric}
                aria-label={config.label}
                className="gap-2"
              >
                <IconComponent className="w-4 h-4" />
                <span className="hidden sm:inline">{config.label}</span>
              </ToggleGroupItem>
            );
          })}
        </ToggleGroup>
      </CardHeader>

      <CardContent>
        {/* Loading状態 */}
        {loading && (
          <div className="flex items-center justify-center h-[240px]">
            <div className="flex flex-col items-center gap-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <span className="text-sm text-muted-foreground">データを読み込み中...</span>
            </div>
          </div>
        )}

        {/* Error状態 */}
        {error && !loading && (
          <div className="h-[240px] flex items-center justify-center">
            <Alert variant="destructive" className="max-w-md">
              <AlertDescription>
                データの読み込みに失敗しました: {error}
              </AlertDescription>
            </Alert>
          </div>
        )}

        {/* グラフ（通常表示） */}
        {!loading && !error && trendData.length > 0 && (
          <div className="h-[240px]">
            <ResponsiveContainer width="100%" height="100%" debounce={10}>
              <LineChart data={trendData} margin={{ top: 20, right: 20, left: 70, bottom: 40 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis
                dataKey="index"
                className="text-muted-foreground text-xs"
                ticks={xAxisTicks}
                tick={{ fontSize: 12 }}
                angle={0}
                textAnchor="middle"
                height={35}
                domain={[0, 36]}
                type="number"
                scale="linear"
                tickFormatter={formatXAxisTick}
                allowDataOverflow={false}
              />
              <YAxis
                className="text-muted-foreground text-xs"
                fontSize={12}
                tickFormatter={currentMetric.format}
                domain={currentMetric.getDomain(trendData)}
              />
              <RechartsTooltip content={<CustomTooltip />} />

              {/* 現在日の区切り線（実績と予測の境界） */}
              <ReferenceLine
                x={29}
                stroke="#f59e0b"
                strokeDasharray="5 5"
                label={{ value: "今日", position: "top" }}
              />

              {/* 実績データライン */}
              <Line
                type="monotone"
                dataKey={selectedMetric + 'Actual'}
                stroke={currentMetric.color}
                strokeWidth={3}
                strokeDasharray="none"
                connectNulls={true}
                dot={false}
                activeDot={{ r: 6, fill: currentMetric.color }}
              />

              {/* 予測データライン */}
              <Line
                type="monotone"
                dataKey={selectedMetric + 'Predicted'}
                stroke="#f59e0b"
                strokeWidth={2}
                strokeDasharray="8 4"
                connectNulls={true}
                dot={false}
                activeDot={{ r: 4, fill: '#f59e0b', opacity: 0.7 }}
              />

              {/* カスタム凡例 - 実績と予測を分離 */}
              <Legend
                content={() => (
                  <div className="flex justify-center gap-2 mb-2 flex-wrap">
                    <Badge
                      variant="outline"
                      className="gap-1"
                      style={{
                        color: currentMetric.color,
                        borderColor: currentMetric.color
                      }}
                    >
                      <CircleIcon className="w-3 h-3" style={{ color: currentMetric.color }} />
                      {currentMetric.label}（実績）
                    </Badge>
                    <Badge
                      variant="outline"
                      className="gap-1"
                      style={{
                        color: '#f59e0b',
                        borderColor: '#f59e0b'
                      }}
                    >
                      <CircleIcon className="w-3 h-3" style={{ color: '#f59e0b' }} />
                      {currentMetric.label}（予測）
                    </Badge>
                  </div>
                )}
              />
            </LineChart>
          </ResponsiveContainer>

          {/* グラフ説明 */}
          <div className="mt-4 pt-4 border-t">
            <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
              <div className="flex items-center gap-1 min-w-0">
                <div className="w-3 h-3 flex-shrink-0 rounded-sm" style={{ backgroundColor: currentMetric.color }}></div>
                <span className="break-words">実績値（過去30日間の完了予約から集計）</span>
              </div>
              <div className="flex items-center gap-1 min-w-0">
                <div className="w-3 h-3 flex-shrink-0 border-2 rounded-sm" style={{ borderColor: '#f59e0b' }}></div>
                <span className="break-words">予測値（今後7日間のAI予測）</span>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* 異常値とイベント表示（データがある場合のみ） */}
        {!loading && !error && trendData.some(d => d.event) && (
          <div className="mt-4">
            <p className="text-xs text-muted-foreground mb-2">
              📌 期間中のイベント
            </p>
            <div className="flex gap-2 flex-wrap">
              {trendData
                .filter(d => d.event)
                .map((d, index) => (
                  <Badge
                    key={index}
                    variant="outline"
                    className="text-xs"
                  >
                    {d.date}: {d.event}
                  </Badge>
                ))
              }
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
